/**
 * AudioPage
 *
 * Top-level "Audio" section. Groups six existing audio-related pages/components
 * under the standardized top sub-tab bar (<SubPageShell> + <SubTabBar>).
 *
 * Each sub-tab renders an EXISTING component unchanged (all take no props, the
 * same way App.tsx renders them today). Only the active sub-tab's body is
 * mounted, and it is wrapped in an <ErrorBoundary> so a crash in one tab cannot
 * take down the whole section.
 *
 * Badges reflect active-generation counts from GenerationContext, keyed by the
 * same id used for each sub-tab (all valid GenerationTabMode values).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Volume2, Globe, Music2, Music, Mic, UserRoundCog } from 'lucide-react';

import { SubPageShell, type SubTabItem } from '@/components/common';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useGeneration } from '@/contexts/GenerationContext.context';
import type { GenerationTabMode } from '@/types/generationJob';

import { AudioGenerationPage } from '@/pages/AudioGenerationPage';
import { MusicGenerationPage } from '@/pages/MusicGenerationPage';
import { SoundEffectsPage } from '@/pages/SoundEffectsPage';
import { DubbingTab } from '@/components/dubbing';
import { isSubtitlesOnlyAvailable } from '@/components/subtitles/availability';
import { SpeechToTextTab } from '@/components/speechtotext';
import { VoiceCloneTab } from '@/components/voiceclone/VoiceCloneTab';

/** Sub-tab ids, in display order. Each is also a valid GenerationTabMode key. */
const SUB_TAB_IDS = ['audio', 'dubbing', 'music', 'soundEffects', 'stt', 'voiceclone'] as const;

type AudioSubTabId = (typeof SUB_TAB_IDS)[number];

const DEFAULT_SUB_TAB: AudioSubTabId = 'audio';

function isAudioSubTabId(value: string): value is AudioSubTabId {
  return (SUB_TAB_IDS as readonly string[]).includes(value);
}

export interface AudioPageProps {
  /**
   * Optional deep-link target sub-tab. When provided and valid, it becomes the
   * initial active sub-tab and is kept in sync via an effect (App.tsx passes
   * this for deep-links).
   */
  initialSubTab?: string;
}

export function AudioPage({ initialSubTab }: AudioPageProps) {
  const { t } = useTranslation();
  const { activeCountsByTab } = useGeneration();

  const [active, setActive] = useState<string>(
    initialSubTab && isAudioSubTabId(initialSubTab) ? initialSubTab : DEFAULT_SUB_TAB
  );

  // React to deep-link changes from the parent (App.tsx).
  useEffect(() => {
    if (initialSubTab && isAudioSubTabId(initialSubTab)) {
      setActive(initialSubTab);
    }
  }, [initialSubTab]);

  const badge = (id: AudioSubTabId): number => activeCountsByTab.get(id as GenerationTabMode) ?? 0;

  const tabs: SubTabItem[] = useMemo(
    () => [
      {
        id: 'audio',
        label: t('app.header.audioGenerationTab', { defaultValue: 'Audio Generation' }),
        icon: Volume2,
        badgeCount: badge('audio'),
      },
      {
        id: 'dubbing',
        // "/ Subtitles" only where the Subtitles Only sub-tab exists (desktop).
        label: isSubtitlesOnlyAvailable()
          ? t('app.header.dubbingTab', 'Dubbing / Subtitles')
          : t('app.header.dubbingOnlyTab', 'Dubbing'),
        icon: Globe,
        badgeCount: badge('dubbing'),
      },
      {
        id: 'music',
        label: t('app.header.musicTab', 'Music'),
        icon: Music2,
        badgeCount: badge('music'),
      },
      {
        id: 'soundEffects',
        label: t('app.header.soundEffectsTab', 'Sound Effects'),
        icon: Music,
        badgeCount: badge('soundEffects'),
      },
      {
        id: 'stt',
        label: t('app.header.sttTab', 'Speech to Text'),
        icon: Mic,
        badgeCount: badge('stt'),
      },
      {
        id: 'voiceclone',
        label: t('app.header.voiceCloneTab', 'Voice Clone'),
        icon: UserRoundCog,
        badgeCount: badge('voiceclone'),
      },
    ],
    // `t` is stable per-language; recompute when language or counts change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, activeCountsByTab]
  );

  const body = useMemo(() => {
    switch (active) {
      case 'dubbing':
        return (
          <ErrorBoundary section="dubbing" key="dubbing">
            <DubbingTab />
          </ErrorBoundary>
        );
      case 'music':
        return (
          <ErrorBoundary section="music" key="music">
            <MusicGenerationPage />
          </ErrorBoundary>
        );
      case 'soundEffects':
        return (
          <ErrorBoundary section="soundEffects" key="soundEffects">
            <SoundEffectsPage />
          </ErrorBoundary>
        );
      case 'stt':
        return (
          <ErrorBoundary section="stt" key="stt">
            <SpeechToTextTab />
          </ErrorBoundary>
        );
      case 'voiceclone':
        return (
          <ErrorBoundary section="voiceclone" key="voiceclone">
            <VoiceCloneTab />
          </ErrorBoundary>
        );
      case 'audio':
      default:
        return (
          <ErrorBoundary section="audio" key="audio">
            <AudioGenerationPage />
          </ErrorBoundary>
        );
    }
  }, [active]);

  return (
    <SubPageShell
      tabs={tabs}
      activeId={active}
      onChange={setActive}
      accent="purple"
      ariaLabel={t('app.header.audioTab', { defaultValue: 'Audio' })}
    >
      {body}
    </SubPageShell>
  );
}

export default AudioPage;
